// Interim API contract for the Agent Detail screen (T6).
// All data is deterministically derived from the agent id — no randomness, so
// re-renders and refreshes (deep-link restore, T6.13) are stable. When the
// FastAPI backend lands (BUILD.md §2), these shapes become the response
// contracts for /api/agents/:id/{tasks,memory,config,performance}.

import { agents } from './dashboard';

const TASK_TITLES = [
  'Competitive intel sweep',
  'Draft weekly digest',
  'Reconcile invoice batch',
  'Analyze campaign funnel',
  'Sync CRM contacts',
  'Rotate API credentials',
  'Summarize support tickets',
  'Refresh pricing benchmarks',
  'Generate onboarding copy',
  'Audit access logs',
  'Cluster churn signals',
  'Publish changelog entry',
  'Validate lead payloads',
  'Backfill analytics events',
  'Review contract redlines',
  'Optimize ad bid caps'
];

const TASK_STATUSES = ['succeeded', 'running', 'failed', 'queued'];

function seededValue(seed, min, max) {
  return min + (seed % (max - min + 1));
}

export function agentExists(agentId) {
  return agents.some((agent) => String(agent.id) === String(agentId));
}

export function agentTasks(agentId) {
  const id = Number(agentId) || 1;
  const count = seededValue(id * 7, 12, 18);

  return Array.from({ length: count }, (_, index) => {
    const seed = id * 31 + index * 17;
    const status = TASK_STATUSES[seed % TASK_STATUSES.length];
    const startedAt = new Date(Date.now() - seed * 47 * 60 * 1000).toISOString();

    return {
      id: `TSK-${1000 + id * 100 + index}`,
      title: TASK_TITLES[seed % TASK_TITLES.length],
      status,
      startedAt,
      durationMin: seededValue(seed * 3, 2, 48),
      tokens: seededValue(seed * 11, 400, 5200)
    };
  });
}

export function agentMemoryStats(agentId) {
  const id = Number(agentId) || 1;
  const chunks = seededValue(id * 97, 400, 2400);

  return {
    chunks,
    dimensions: 384,
    indexHealth: 'healthy',
    lastIndexedAt: new Date(Date.now() - seededValue(id * 13, 5, 300) * 60 * 1000).toISOString()
  };
}

export function agentMemory(agentId) {
  const id = Number(agentId) || 1;
  const focus = agents.find((agent) => String(agent.id) === String(agentId))?.role || 'Operations';

  return [
    {
      id: `fact-${id}-1`,
      text: `${focus} outputs follow the agency style guide v3.`,
      pinned: true,
      updatedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString()
    },
    {
      id: `fact-${id}-2`,
      text: 'Escalate to a human operator before any spend above $5,000.',
      pinned: true,
      updatedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
    },
    {
      id: `fact-${id}-3`,
      text: `Preferred channel for ${focus.toLowerCase()} summaries is email.`,
      pinned: false,
      updatedAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString()
    },
    {
      id: `fact-${id}-4`,
      text: 'Retry failed jobs at most twice, then queue for review.',
      pinned: false,
      updatedAt: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString()
    }
  ];
}

export function agentConfig(agentId) {
  const id = Number(agentId) || 1;

  return {
    systemPrompt: [
      `You are ${id === 1 ? 'the agency orchestrator' : 'a focused agency agent'}.`,
      'Operate autonomously within approved guardrails.',
      'Escalate ambiguous or high-impact actions to the approval queue.',
      'Log every decision with a one-line rationale.'
    ].join('\n'),
    tools: [
      { id: 'browser', label: 'Browser automation', enabled: id % 2 === 1 },
      { id: 'search', label: 'Web search', enabled: true },
      { id: 'sql', label: 'SQL warehouse', enabled: id % 3 === 0 },
      { id: 'email', label: 'Email dispatch', enabled: false },
      { id: 'files', label: 'File storage', enabled: true }
    ],
    models: [
      { id: 'glm-5.2', label: 'GLM-5.2' },
      { id: 'glm-5-turbo', label: 'GLM-5 Turbo' },
      { id: 'glm-4.7', label: 'GLM-4.7' },
      { id: 'deepseek-v4-flash', label: 'DeepSeek V4 Flash' }
    ],
    selectedModelId: id % 2 === 0 ? 'glm-4.7' : 'glm-5.2'
  };
}

export function agentPerformance(agentId) {
  const id = Number(agentId) || 1;
  const days = 14;

  const labels = Array.from({ length: days }, (_, index) => {
    const date = new Date(Date.now() - (days - 1 - index) * 24 * 60 * 60 * 1000);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  });

  const successRate = Array.from({ length: days }, (_, index) => {
    const seed = id * 13 + index * 5;
    return 62 + (seed % 37);
  });

  const cost = Array.from({ length: days }, (_, index) => {
    const seed = id * 29 + index * 7;
    return Number((1.2 + (seed % 41) / 10).toFixed(2));
  });

  return { labels, successRate, cost };
}

export function agentLogLines(agentId) {
  const role = agents.find((agent) => String(agent.id) === String(agentId))?.role || 'agent';

  return [
    `[${role}] Scanning workspace for pending work items`,
    `[${role}] Retrieved 12 relevant memory chunks`,
    `[${role}] Drafting plan — 4 steps, est. 8m`,
    `[${role}] Tool call ok: search.query (240ms)`,
    `[${role}] Guardrail check passed — impact $${120 + (Number(agentId) || 1) * 40}`,
    `[${role}] Step 2 complete — wrote draft to S3`,
    `[${role}] Waiting on sibling agent output`,
    `[${role}] Token budget at 61% — pacing normally`
  ];
}
